import axios from "axios";
import { registerTypeIcons } from "./lib";

const BASE = (
  process.env.REACT_APP_API_BASE_URL || "https://api.huay-huay.vip"
).replace(/\/$/, "");

// axios singleton carries the player's Bearer token (set by utils/authSession.setSession)
const api = axios.create({ baseURL: BASE });
api.interceptors.request.use((cfg) => {
  const t =
    (typeof window !== "undefined" &&
      (localStorage.getItem("accessToken") ||
        localStorage.getItem("authToken"))) ||
    null;
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// backend เวอร์ชันเก่ายังไม่มี GET /countdownbox/status (ตอบ 404) → ประกอบเองจาก setting (หน่วยนาที) + userlist.countdown_end
// ไม่มี server_now จึงใช้นาฬิกาเครื่องแทน — พอสำหรับช่วงรอ deploy backend ใหม่ (ตัวจริงชดเชยเวลาเซิร์ฟเวอร์ให้)
async function countdownStatusLegacy(uid) {
  const [setting, user] = await Promise.all([
    api.get("/countdownbox/setting").then((r) => r.data?.data?.[0] ?? null),
    api
      .get(`/usersystem/${uid}`)
      .then((r) => (Array.isArray(r.data?.data) ? r.data.data[0] : null)),
  ]);
  if (!setting || !user) return null;
  const minutes = Number(setting.countdown_time) || 0;
  const end = user.countdown_end ? new Date(user.countdown_end) : null;
  const valid = !!end && !Number.isNaN(end.getTime());
  return {
    point_value: setting.point_value,
    countdown_minutes: minutes,
    countdown_seconds: Math.round(minutes * 60),
    countdown_end: valid ? end.toISOString() : null,
    server_now: null,
    can_claim: !valid || end <= new Date(),
  };
}

const unwrap = (r) => (r && r.data && "data" in r.data ? r.data.data : r?.data);

export const lottoApi = {
  base: BASE,

  // ---- public ----
  types: () =>
    api
      .get("/lotto/types")
      .then(unwrap)
      .then((list) => {
        registerTypeIcons(list, BASE); // ไอคอนหวยที่แอดมินอัปโหลด → ใช้กับ FlagBadge ทุกหน้า
        return list;
      }),
  features: () => api.get("/lotto/features").then(unwrap),
  // หน้าตาเว็บ (โทนสี / โหมดมืด-สว่าง / ข้อความ / ส่วนที่แสดง) ที่แอดมินตั้ง — ดู landingTheme.js
  landingConfig: () => api.get("/lotto/landing-config").then(unwrap),
  webSetting: () =>
    api.get("/setting/websetting").then((r) => r.data?.data?.[0] || null),
  openRounds: () => api.get("/lotto/rounds/open").then(unwrap),
  upcomingRounds: () => api.get("/lotto/rounds/upcoming").then(unwrap),
  awaitingRounds: () => api.get("/lotto/rounds/awaiting").then(unwrap),
  round: (id) => api.get(`/lotto/rounds/${id}`).then(unwrap),
  results: (params = {}) => api.get("/lotto/results", { params }).then(unwrap),

  // ---- player (auth) ----
  placeBet: (payload) => api.post("/lotto/bets", payload).then((r) => r.data),
  myBets: (params = {}) => api.get("/lotto/bets/mine", { params }).then(unwrap),
  cancelBet: (id) => api.post(`/lotto/bets/${id}/cancel`).then((r) => r.data),

  // ---- wallet / account ----
  balance: (uid) =>
    api.get(`/userbalance/balance/${uid}`).then((r) => {
      const d = r.data;
      if (typeof d === "number") return d;
      return Number(d?.balance ?? d?.data ?? 0) || 0;
    }),
  ledger: (uid, params = {}) =>
    api
      .get(`/userbalance/history/${uid}`, { params })
      .then((r) => r.data?.data ?? r.data ?? []),

  // ---- deposit / withdraw ----
  depositBanks: () =>
    api.get("/bank").then((r) => r.data?.data ?? r.data ?? []),
  slipSetting: () =>
    api
      .get("/slip/setting")
      .then((r) => Boolean(r.data?.data?.customerSlipUploadEnabled))
      .catch(() => false),
  slipAccounts: () =>
    api
      .get("/slip/accounts")
      .then((r) => r.data?.data ?? [])
      .catch(() => []),
  slipHistory: (userId) =>
    api
      .get("/slip/history", { params: { userId, page: 1, limit: 10 } })
      .then((r) => r.data?.data ?? [])
      .catch(() => []),
  slipVerify: (body) => api.post("/slip/verify", body).then((r) => r.data),
  gsbAccounts: () =>
    api
      .get("/gsbauto/accounts")
      .then((r) => r.data?.data ?? [])
      .catch(() => []),
  depositHistory: (uid) =>
    api
      .get(`/userbalance/history/${uid}`)
      .then((r) => r.data?.data ?? r.data ?? []),
  withdrawHistory: (uid) =>
    api
      .get(`/userbalance/withdrawhistory/${uid}`)
      .then((r) => r.data?.data ?? r.data ?? []),
  depositQr: (amount) =>
    api
      .post("/onpay/deposit/create-qr-code", { amount: String(amount) })
      .then((r) => r.data)
      .catch(() =>
        api
          .post("/bibbyx/create-charge", { amount: Number(amount) })
          .then((r) => r.data),
      ),
  // amount = จำนวนเต็มบาท (เซิร์ฟเวอร์ตรวจซ้ำ: ไม่มีทศนิยม, ขั้นต่ำ 50) · ไม่ส่ง = ถอนทั้งหมด
  withdraw: (uid, amount) => {
    const body = new URLSearchParams();
    body.append("message", "withdraw request");
    if (amount !== undefined) body.append("amount", String(amount));
    return api
      .post(`/8hwe8vhevv/requestwithdraw/${uid}`, body)
      .then((r) => r.data);
  },

  // ---- auth ----
  checkRegister: (body) =>
    api.post("/usersystem/checkregister", body).then((r) => r.data),
  register: (body) => api.post("/usersystem", body).then((r) => r.data),
  transferBanks: () =>
    api.get("/bank/transferbanklist").then((r) => r.data?.data ?? r.data ?? []),

  // ---- เครดิตฟรี (แคมเปญที่ลูกค้ากดรับเอง) ----
  // ไม่ล็อกอินก็ดูได้ (state = LOGIN) · ล็อกอินแล้วได้สถานะรายคน (CLAIMABLE / CLAIMED / NOT_ELIGIBLE / FULL)
  freeCredits: () => api.get("/lotto/free-credit").then(unwrap),
  claimFreeCredit: (id) =>
    api.post(`/lotto/free-credit/${id}/claim`).then((r) => r.data),
  freeCreditLimits: () => api.get("/lotto/free-credit/limits").then(unwrap),
  // แถบเตือนติดเทิร์น (ต้องล็อกอิน) = { active, balance, bonus_locked, withdrawable, legacy, pending_count, remaining, required_total, done_total, items[{src,label,name,amount,required,done,remaining,pct,since}] }
  turnStatus: () => api.get("/lotto/turn-status").then(unwrap),
  myFreeCredits: () => api.get("/lotto/free-credit/mine").then(unwrap),

  // ---- เครดิตโค้ด (กรอกรหัสรับเครดิต/เพชร) ----
  // ต้องล็อกอิน · myCreditCode = { enabled, history[], active[] } · redeem คืน {success, message, rewardType, rewardAmount, ...} หรือ 4xx พร้อม {code, message}
  myCreditCode: () => api.get("/lotto/creditcode").then(unwrap),
  redeemCreditCode: (code) =>
    api.post("/lotto/creditcode/redeem", { code }).then((r) => r.data),

  // ---- แรงค์สมาชิก ---- ต้องล็อกอิน · myRanking = { enabled, current, next, ladder[], pending[], history[], active[] }
  myRanking: () => api.get("/lotto/ranking").then(unwrap),
  claimRank: (id) =>
    api.post("/lotto/ranking/claim", { id }).then((r) => r.data),

  // ---- คืนยอดเสีย ---- ต้องล็อกอิน · myCashback = { enabled, setting, rules[], today, pending[], history[], active[], week[], totals } · claimCashback(id|null) null = รับทั้งหมด
  myCashback: () => api.get("/lotto/cashback").then(unwrap),
  claimCashback: (id) =>
    api
      .post(
        "/lotto/cashback/claim",
        id === null || id === undefined ? {} : { id },
      )
      .then((r) => r.data),

  // ---- เช็คอินรายวัน (ใหม่) ---- ต้องล็อกอิน · myCheckin = { enabled, state, event, days[], progress, today_claimed, seconds_to_reset, totals, rules[], history[], active[] }
  myCheckin: () => api.get("/lotto/checkin").then(unwrap),
  claimCheckin: () => api.post("/lotto/checkin/claim", {}).then((r) => r.data),

  // ---- กิจกรรมการตลาด (อ่านบทความ/ดูคลิป ตอบคำถามรับรางวัล) ---- ต้องล็อกอิน · myActivities = { enabled, activities[] (ไม่มีเฉลย), history[], active[], totals, seconds_to_reset }
  // submitActivity คืน { success, correct, message, attempts_left, reward? } (ตอบผิดไม่ใช่ error) หรือ 4xx พร้อม {code, message}
  myActivities: () => api.get("/lotto/marketing-activity").then(unwrap),
  submitActivity: (id, answers) =>
    api
      .post(`/lotto/marketing-activity/${id}/submit`, { answers })
      .then((r) => r.data),

  // ---- รีวิวลูกค้า ---- publicReviews = { enabled, data[{id,name(ปิดบังแล้ว),rating,message,pinned,created_at}], summary{total,avg,distribution,percent}, pagination } (ไม่ต้องล็อกอิน)
  // myReviews (ล็อกอิน) = { enabled, can_submit{ok,code,message,wait_seconds}, items[] } · submitReview ส่งแล้วรอแอดมินอนุมัติ หรือ 4xx พร้อม {code, message, wait_seconds}
  publicReviews: (page = 1, limit = 8) =>
    api.get("/reviews/public", { params: { page, limit } }).then((r) => r.data),
  myReviews: () => api.get("/lotto/reviews/me").then(unwrap),
  submitReview: (rating, message) =>
    api.post("/lotto/reviews", { rating, message }).then((r) => r.data),

  // ---- เช็คอินรายวัน ----
  checkinActive: (userId) =>
    api
      .get("/checkin/active", { params: { userId } })
      .then((r) => r.data?.data ?? null),
  checkinClaim: (eventId, userId) =>
    api.post("/checkin/active/claim", { eventId, userId }).then((r) => r.data),

  // ---- กงล้อพารวย ----
  wheelSettings: () =>
    api.get("/setting/wheelsetting").then((r) => r.data?.data?.[0] ?? null),
  wheelPointOf: (uid) =>
    api.get(`/wheel/point/${uid}`).then((r) => {
      const d = r.data;
      if (typeof d === "number") return d;
      return Number(d?.data?.[0]?.wheelPoint ?? d?.wheelPoint ?? 0) || 0;
    }),
  wheelSpin: (uid) => api.post(`/wheel/spining/${uid}`).then((r) => r.data),

  // ---- กล่องรับเพชร (นับถอยหลัง) + ประวัติแต้มเพชร ----
  // status = { point_value, countdown_minutes, countdown_seconds, countdown_end, server_now, can_claim } (ค่าตามที่ตั้งใน /wheeling)
  // ค่าที่แอดมินตั้ง (ไม่ต้องล็อกอิน): { point_value, countdown_time (นาที) }
  countdownBoxSetting: () =>
    api.get("/countdownbox/setting").then((r) => r.data?.data?.[0] ?? null),
  countdownBoxStatus: (uid) =>
    api
      .get(`/countdownbox/status/${uid}`)
      .then((r) => r.data?.data ?? null)
      .catch((e) => {
        if (e?.response?.status === 404) return countdownStatusLegacy(uid);
        throw e;
      }),
  countdownBoxReceive: (uid) =>
    api.post(`/countdownbox/receive/${uid}`).then((r) => r.data),
  // { data: [{id, amount, balance_after, source, note, created_at}], hasMore, summary: {totalReceived, count} }
  diamondHistory: (uid, params = {}) =>
    api.get(`/wheel/diamond-history/${uid}`, { params }).then((r) => r.data),

  // ---- โปรโมชั่น ----
  userDetail: (uid) =>
    api.get(`/usersystem/${uid}`).then((r) => {
      const d = r.data;
      return Array.isArray(d?.data) ? d.data[0] : (d?.data ?? d ?? null);
    }),
  availablePromotions: (uid) =>
    api
      .get(`/usersystem/getavailablepromotion/${uid}`)
      .then((r) => r.data?.data ?? r.data ?? []),
  selectPromotion: (uid, promotionId, detail) => {
    const body = new URLSearchParams();
    body.set("PromotionID", promotionId);
    body.set("PromotionDetail", detail);
    return api
      .put(`/usersystem/promotion/${uid}`, body.toString(), {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
      .then((r) => r.data);
  },

  // ---- แนะนำเพื่อน ----
  inviteSetting: () =>
    api.get("/setting/invite").then((r) => r.data?.data?.[0] ?? null),
  inviteSummary: (uid) =>
    api
      .get(`/agentbalance/invitesummaryclient/${uid}`)
      .then((r) => r.data?.data ?? []),
  invitedUsers: (uid) =>
    api
      .get(`/agentbalance/invitedusers/${uid}`)
      .then((r) => r.data?.data ?? []),
  claimInvite: (uid, inviteId) =>
    api
      .post(`/agentbalance/paidinvite/${uid}`, { data: inviteId })
      .then((r) => r.data),
};

export default lottoApi;
